import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Alert, Flex, Table, Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { useTranslation } from 'react-i18next';
import DownloadIcon from '@/assets/icons/download-01.svg?react';
import UploadIcon from '@/assets/icons/upload-01.svg?react';
import { Button } from '@/components/button/button';
import FactTile from '@/components/fact-tile/fact-tile';
import { FormModal } from '@/components/form-modal/form-modal';
import { formatNumber } from '@/i18n/format';
import { useCampaignImport } from '@/hooks/use-outbound-campaigns/use-outbound-campaigns';
import { getCampaignImportTemplate } from '@/services/outbound-calls-service/campaigns.service';
import {
  CampaignImportReport,
  CampaignImportSkippedRow,
} from '@/services/outbound-calls-service/campaigns.service.types';
import { apiFieldErrors } from '@/utils/api-field-errors';
import { downloadFile } from '@/utils/helpers';
import { openNotification } from '@/utils/notification/notification';
import {
  importFileErrorLabel,
  outboundErrorMessage,
  skipReasonLabel,
} from '../outbound-calls.messages';
import styles from '../outbound-calls.module.scss';
import { importReportCsv, issueCounts } from '../outbound-calls.utils';

interface CampaignImportModalProps {
  open: boolean;
  campaignId: number;
  onClose: () => void;
}

/** Upload, a dry run that reports what would happen, then the real import of the same file. */
export function CampaignImportModal({ open, campaignId, onClose }: CampaignImportModalProps) {
  const { t } = useTranslation(['outbound', 'common']);
  const { importList, isPending, reset } = useCampaignImport(campaignId);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [report, setReport] = useState<CampaignImportReport | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (open) return;
    setFileList([]);
    setReport(null);
    setErrors([]);
    reset();
  }, [open, reset]);

  const file = fileList[0]?.originFileObj as File | undefined;
  const addedUp = [...(report?.skipped ?? []), ...(report?.warnings ?? [])].some(
    (row) => row.code === 'summed_numbers'
  );

  const run = async (dryRun: boolean) => {
    if (!file) return;
    setErrors([]);
    try {
      const result = await importList({ file, dryRun });
      if (dryRun) {
        // A CRM from before the report had warnings sends none.
        setReport({ ...result, warnings: result.warnings ?? [] });
        return;
      }
      openNotification({
        type: 'success',
        message: t('outbound:import.done', { entries: formatNumber(result.entry_count) }),
      });
      onClose();
    } catch (requestError) {
      const fileErrors = apiFieldErrors(requestError)?.file;
      setErrors(
        fileErrors
          ? fileErrors.map(importFileErrorLabel)
          : [outboundErrorMessage(requestError, t('outbound:import.failed'))]
      );
    }
  };

  const downloadReport = () => {
    if (!report || !file) return;
    downloadFile({
      blob: new Blob([importReportCsv(report)], { type: 'text/csv;charset=utf-8' }),
      fileName: t('outbound:import.reportFileName', { name: file.name.replace(/\.csv$/i, '') }),
    });
  };

  const downloadTemplate = async () => {
    setIsDownloading(true);
    try {
      downloadFile(await getCampaignImportTemplate());
    } catch {
      openNotification({ type: 'error', message: t('outbound:import.templateFailed') });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <FormModal
      open={open}
      title={t('outbound:import.title')}
      subtitle={t('outbound:import.hint')}
      onCancel={onClose}
      width={640}
      isLoading={isPending}
      submitDisabled={report ? report.entry_count === 0 : !file}
      submitLabel={report ? t('outbound:import.confirm') : t('outbound:import.check')}
      onSubmit={() => run(!report)}
    >
      <Flex vertical gap={14}>
        <div className={clsx(styles.actions, styles.uploadRow)}>
          <Upload
            accept=".csv,text/csv"
            fileList={fileList}
            maxCount={1}
            beforeUpload={() => false}
            onChange={(info) => {
              setFileList(info.fileList.slice(-1));
              // A new file has not been checked yet.
              setReport(null);
              setErrors([]);
            }}
          >
            <Button buttonVariant="secondary" icon={<UploadIcon className={styles.buttonIcon} />}>
              {t('outbound:import.pickFile')}
            </Button>
          </Upload>
          <Button
            buttonVariant="secondary"
            icon={<DownloadIcon className={styles.buttonIcon} />}
            loading={isDownloading}
            onClick={downloadTemplate}
          >
            {t('outbound:import.template')}
          </Button>
        </div>

        {errors.map((message) => (
          <Alert key={message} type="error" showIcon message={message} />
        ))}

        {report && (
          <Flex vertical gap={10}>
            <span className={styles.label}>{t('outbound:import.previewTitle')}</span>
            <div className={styles.facts}>
              <FactTile
                label={t('outbound:import.leads')}
                value={formatNumber(report.lead_count)}
              />
              <FactTile
                label={t('outbound:import.contacts')}
                value={formatNumber(report.contact_count)}
              />
              <FactTile
                label={t('outbound:import.entries')}
                value={formatNumber(report.entry_count)}
              />
              <FactTile
                label={t('outbound:import.alreadyInCampaign')}
                value={formatNumber(report.already_in_campaign)}
              />
            </div>
            {report.entry_count === 0 && (
              <Alert type="warning" showIcon message={t('outbound:import.nothingToImport')} />
            )}
            {addedUp && <Alert type="warning" showIcon message={t('outbound:import.summedHint')} />}
            <span className={styles.label}>{t('outbound:import.skippedTitle')}</span>
            {report.skipped.length ? (
              <ReportRows rows={report.skipped} />
            ) : (
              <p className={styles.secondary}>{t('outbound:import.skippedNone')}</p>
            )}
            {report.warnings.length > 0 && (
              <>
                <span className={styles.label}>{t('outbound:import.warningsTitle')}</span>
                <ReportRows rows={report.warnings} />
              </>
            )}
            {(report.skipped.length > 0 || report.warnings.length > 0) && (
              <div>
                <Button
                  buttonVariant="secondary"
                  icon={<DownloadIcon className={styles.buttonIcon} />}
                  onClick={downloadReport}
                >
                  {t('outbound:import.downloadReport')}
                </Button>
              </div>
            )}
          </Flex>
        )}
      </Flex>
    </FormModal>
  );
}

/** Rows of the report, counted by issue and filterable by it: a real list skips hundreds. */
function ReportRows({ rows }: { rows: CampaignImportSkippedRow[] }) {
  const { t } = useTranslation('outbound');
  const counts = issueCounts(rows);

  return (
    <>
      <ul className={styles.issueCounts}>
        {counts.map(({ code, count }) => (
          <li key={code}>
            {t(`import.issues.${code}`)}: <strong>{formatNumber(count)}</strong>
          </li>
        ))}
      </ul>
      <Table
        size="small"
        rowKey={(row) => `${row.line}-${row.reason}`}
        pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        dataSource={rows}
        columns={[
          { title: t('import.line'), dataIndex: 'line', key: 'line', width: 90 },
          {
            title: t('import.reason'),
            dataIndex: 'reason',
            key: 'reason',
            render: (reason: string) => skipReasonLabel(reason),
            filters:
              counts.length > 1
                ? counts.map(({ code }) => ({ text: t(`import.issues.${code}`), value: code }))
                : undefined,
            onFilter: (code, row) => row.code === code,
          },
        ]}
      />
    </>
  );
}
